import React from 'react';
import { motion } from 'framer-motion';

// Données pour la timeline
const timelineData = [
  { year: '2024', text: 'Création du cabinet juridique KMJ SCP.' },
  { year: '2025', text: 'Expansion avec l\'ouverture d\'un deuxième bureau régional.' },
 
];

// Données pour les valeurs (Suppression des gradients violets)
const values = [
  {
    icon: '⚖️',
    title: 'Justice',
    text: 'Nous plaçons l\'équité au cœur de chaque action stratégique menée.',
  },
  {
    icon: '🤝',
    title: 'Intégrité',
    text: 'Une déontologie irréprochable garantissant une transparence totale.',
  },
  {
    icon: '📚',
    title: 'Expertise',
    text: 'Une équipe hautement qualifiée dédiée à l\'excellence juridique.',
  },
  {
    icon: '💼',
    title: 'Rigueur',
    text: 'Un accompagnement chirurgical et personnalisé pour chaque client.',
  },
];

const AboutSection = () => {
  const galleryImages = [
    '/images/L1.webp',
    '/images/L2.png',
    '/images/L3.webp',
    '/images/L4.jpg',
  ];

  // Animations Premium (Bézier Curve)
  const premiumTransition = { duration: 1, ease: [0.16, 1, 0.3, 1] };

  const fadeUpVariants = {
    hidden: { y: 40, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: premiumTransition }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
  };

  return (
    <section className="about-section">
      {/* Texture de fond luxueuse */}
      <div className="noise-overlay" />

      {/* Hero Section */}
      <div className="about-hero">
        <div className="hero-content">
          <motion.div 
            className="hero-text-container"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.div className="title-container" variants={fadeUpVariants}>
              <div className="title-line" />
              <h1 className="main-title">
                Notre <span className="title-highlight">Histoire.</span>
              </h1>
              <span className="subtitle">
                3 ans d'excellence juridique.
              </span>
            </motion.div>
            
            <motion.div className="text-content" variants={fadeUpVariants}>
              <p className="hero-text">
                Fort de plus de sept années d'expérience collective, notre cabinet s'engage à fournir des services juridiques d'une qualité exceptionnelle, alliant rigueur, discrétion et stratégie de haut niveau.
              </p>
              <p className="hero-text">
                Nous intervenons dans le conseil stratégique et la représentation de nos clients sur des problématiques complexes. Spécialisés en droit des affaires, nous disposons d'une expertise reconnue en droit commercial, structuration des sociétés et contentieux économique.
              </p>
            </motion.div>
          </motion.div>

          <motion.div 
            className="hero-image"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={premiumTransition}
            viewport={{ once: true }}
          >
            <div className="image-background" />
            <div className="image-wrapper">
              <img src="/images/about2.jpeg" alt="Notre histoire" className="image" loading="lazy" />
              <div className="image-overlay-gradient" />
              <div className="floating-badge">
                <span className="badge-icon">🏛️</span>
                <div className="badge-text-group">
                  <span className="badge-title">Fondation KMJ</span>
                  <span className="badge-subtitle">Depuis 2024</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Timeline Section */}
        <div className="timeline-section">
          <motion.div 
            className="section-header"
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUpVariants}
          >
            <h2 className="section-title">
              Notre <span className="title-highlight">Parcours</span>
            </h2>
            <p className="section-subtitle">
              Les étapes fondatrices de notre développement institutionnel.
            </p>
          </motion.div>

          <div className="timeline-container">
            <div className="timeline-center-line" />
            {timelineData.map((item, index) => (
              <motion.div
                key={index}
                className="timeline-card"
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={{
                  hidden: { opacity: 0, y: 30 },
                  visible: { opacity: 1, y: 0, transition: { ...premiumTransition, delay: index * 0.2 } }
                }}
                whileHover={{ x: 5, borderColor: '#A3947F' }}
              >
                <div className="timeline-dot-wrapper">
                  <div className="timeline-dot" />
                </div>
                <div className="timeline-content">
                  <h3 className="timeline-year">{item.year}</h3>
                  <p className="timeline-text">{item.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Gallery Grid */}
        <div className="gallery-section">
          <motion.div 
            className="section-header"
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUpVariants}
          >
            <h2 className="section-title">
              Notre <span className="title-highlight">Environnement</span>
            </h2>
            <p className="section-subtitle">
              L'espace de réflexion et de stratégie de nos équipes.
            </p>
          </motion.div>

          <div className="gallery-grid">
            {galleryImages.map((img, index) => (
              <motion.div
                key={index}
                className="gallery-item"
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={{
                  hidden: { opacity: 0, scale: 0.95 },
                  visible: { opacity: 1, scale: 1, transition: { ...premiumTransition, delay: index * 0.15 } }
                }}
              >
                <div className="gallery-image-wrapper">
                  <img src={img} alt={`Gallery ${index + 1}`} className="gallery-image" loading="lazy" />
                  <div className="gallery-gradient" />
                  <div className="gallery-overlay">
                    <h3 className="gallery-title">Pôle Expertise</h3>
                    <div className="gallery-line" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Values Section */}
      <div className="values-section">
        <motion.div 
          className="section-header"
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUpVariants}
        >
          <h2 className="section-title">
            Nos <span className="title-highlight">Valeurs</span>
          </h2>
          <p className="section-subtitle">
            L'éthique et la rigueur qui régissent notre pratique quotidienne.
          </p>
        </motion.div>

        <motion.div 
          className="values-grid"
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}
        >
          {values.map((value, index) => (
            <motion.div
              key={index}
              className="value-card"
              variants={fadeUpVariants}
              whileHover={{ y: -5, borderColor: '#A3947F' }}
            >
              <div className="value-icon">{value.icon}</div>
              <h3 className="value-title">{value.title}</h3>
              <p className="value-text">{value.text}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* --- CSS Intégré (Thème Dark Gray & Beige) --- */}
      <style jsx>{`
        .about-section {
          background: #121214;
          color: #F5EFEB;
          padding: 8rem 5%;
          position: relative;
          overflow: hidden;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .noise-overlay {
          position: absolute;
          inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.04'/%3E%3C/svg%3E");
          pointer-events: none;
          z-index: 1;
        }

        .about-hero {
          max-width: 1440px;
          margin: 0 auto;
          position: relative;
          z-index: 2;
        }

        /* --- Headers Communs --- */
        .section-header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .title-line {
          width: 60px;
          height: 1px;
          background: #D4C5B0;
          margin-bottom: 2rem;
        }

        .main-title, .section-title {
          font-size: clamp(2.5rem, 4vw, 4rem);
          font-weight: 300;
          margin-bottom: 1rem;
          line-height: 1.1;
          letter-spacing: -1px;
          color: #F5EFEB;
        }

        .title-highlight {
          color: #D4C5B0;
          font-style: italic;
        }

        .subtitle, .section-subtitle {
          font-size: 1.2rem;
          color: #A0A0A5;
          font-weight: 300;
          display: block;
          max-width: 600px;
          margin: 0 auto;
        }

        /* --- Hero --- */
        .hero-content {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 5rem;
          align-items: center;
          margin-bottom: 8rem;
        }

        .hero-text {
          font-size: 1.05rem;
          line-height: 1.8;
          color: #A0A0A5;
          margin-bottom: 1.5rem;
          font-weight: 300;
          padding-left: 1.5rem;
          border-left: 1px solid rgba(212, 197, 176, 0.15);
        }

        .hero-image {
          position: relative;
          padding: 20px;
        }

        .image-background {
          position: absolute;
          top: 20px; left: 20px; right: -20px; bottom: -20px;
          border: 1px solid #A3947F;
          border-radius: 2px;
          z-index: -1;
        }

        .image-wrapper {
          position: relative;
          height: 550px;
          border-radius: 2px;
          overflow: hidden;
        }

        .image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          filter: grayscale(25%) contrast(1.1) brightness(0.85);
        }

        .image-overlay-gradient {
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, rgba(18,18,20,0.8) 0%, transparent 40%);
        }

        .floating-badge {
          position: absolute;
          bottom: 0; left: 0;
          background: #1C1C1F;
          padding: 1.5rem 2rem;
          border-top: 1px solid rgba(212, 197, 176, 0.15);
          border-right: 1px solid rgba(212, 197, 176, 0.15);
          display: flex;
          align-items: center;
          gap: 1.2rem;
          z-index: 2;
        }

        .badge-icon {
          font-size: 1.8rem;
          filter: grayscale(100%) sepia(100%) hue-rotate(5deg) saturate(300%) brightness(0.9);
        }

        .badge-text-group {
          display: flex;
          flex-direction: column;
          gap: 0.2rem;
        }

        .badge-title {
          color: #D4C5B0;
          font-size: 1rem;
          font-family: 'Playfair Display', serif;
        }

        .badge-subtitle {
          color: #A0A0A5;
          font-size: 0.8rem;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        /* --- Timeline Section --- */
        .timeline-section {
          margin: 8rem auto;
          max-width: 800px;
          position: relative;
        }

        .timeline-container {
          position: relative;
          padding-left: 40px;
        }

        .timeline-center-line {
          position: absolute;
          left: 0; top: 0; bottom: 0;
          width: 1px;
          background: rgba(212, 197, 176, 0.15);
        }

        .timeline-card {
          position: relative;
          margin-bottom: 3rem;
          background: #17171A;
          border: 1px solid rgba(212, 197, 176, 0.15);
          padding: 2rem;
          border-radius: 2px;
          transition: border-color 0.4s ease, transform 0.4s ease;
        }

        .timeline-dot-wrapper {
          position: absolute;
          left: -45px;
          top: 30px;
          background: #121214;
          padding: 10px 0;
        }

        .timeline-dot {
          width: 8px;
          height: 8px;
          background: #D4C5B0;
          transform: rotate(45deg); /* Losange élégant */
        }

        .timeline-year {
          font-size: 2rem;
          color: #D4C5B0;
          margin: 0 0 0.5rem 0;
          font-family: 'Playfair Display', serif;
          font-weight: 400;
        }

        .timeline-text {
          font-size: 1rem;
          color: #A0A0A5;
          line-height: 1.6;
          margin: 0;
          font-weight: 300;
        }

        /* --- Gallery Section --- */
        .gallery-section {
          margin: 8rem auto;
        }

        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.5rem;
        }

        .gallery-item {
          border-radius: 2px;
          overflow: hidden;
          height: 400px;
          border: 1px solid rgba(212, 197, 176, 0.15);
          padding: 10px 10px 0 10px; /* Passe-partout effect */
          background: #17171A;
        }

        .gallery-image-wrapper {
          position: relative;
          width: 100%;
          height: 100%;
          overflow: hidden;
        }

        .gallery-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.8s ease, filter 0.8s ease;
          filter: grayscale(100%) contrast(1.1) brightness(0.85);
        }

        .gallery-item:hover .gallery-image {
          transform: scale(1.05);
          filter: grayscale(0%) contrast(1.1) brightness(0.9);
        }

        .gallery-gradient {
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, rgba(18,18,20,1) 0%, transparent 60%);
        }

        .gallery-overlay {
          position: absolute;
          bottom: 0; left: 0; right: 0;
          padding: 1.5rem;
        }

        .gallery-title {
          font-size: 1.1rem;
          margin: 0 0 0.5rem 0;
          color: #D4C5B0;
          font-weight: 400;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .gallery-line {
          width: 30px; height: 1px;
          background: #A3947F;
        }

        /* --- Values Section --- */
        .values-section {
          margin: 8rem auto 4rem;
        }

        .values-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 2rem;
        }

        .value-card {
          background: #1C1C1F;
          border: 1px solid rgba(212, 197, 176, 0.15);
          border-radius: 2px;
          padding: 3rem 2rem;
          text-align: center;
          transition: all 0.4s ease;
        }

        .value-icon {
          font-size: 2.5rem;
          margin-bottom: 1.5rem;
          /* Effet Bronze sur Emojis */
          filter: grayscale(100%) sepia(100%) hue-rotate(5deg) saturate(300%) brightness(0.9);
        }

        .value-title {
          font-size: 1.4rem;
          color: #F5EFEB;
          margin: 0 0 1rem 0;
          font-weight: 400;
          font-family: 'Playfair Display', serif;
        }

        .value-text {
          font-size: 0.95rem;
          color: #A0A0A5;
          line-height: 1.6;
          font-weight: 300;
          margin: 0;
        }

        /* --- Responsive --- */
        @media (max-width: 1024px) {
          .hero-content {
            gap: 3rem;
            grid-template-columns: 1fr;
          }
          .image-wrapper { height: 450px; }
        }

        @media (max-width: 768px) {
          .about-section { padding: 5rem 1.5rem; }
          .timeline-container { padding-left: 30px; }
          .timeline-dot-wrapper { left: -35px; }
        }
      `}</style>
    </section>
  );
};

export default AboutSection;